// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportConfigurationAction.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { withHandlers } from 'recompose';
import CommonMessages from 'share/messages/Izenda.CommonMessages';
import localizer from 'share/localization/Izenda.Localizer';
import constants from 'logic/data/Izenda.SystemConfigurationConstants';
import { curry } from 'lodash/fp';

export default withHandlers({
  onSave: curry(onSave),
  onCancel: curry(onCancel),
  onChangeTime: curry(onChangeTime),
  onClearArchivedVersion: curry(onClearArchivedVersion),
  setEnforceVersionHistory: curry(setEnforceVersionHistory),
  setNumOfArchivedVersionToKeep: curry(setNumOfArchivedVersionToKeep),
  defaultImageUrlHander: curry(defaultImageUrlHander),
  onCheckRemoveArchivedVersions: curry(onCheckRemoveArchivedVersions)
});

/**
 * On cancel handler
 * @param {Object} props
 * @param {Object} e
 */
function onCancel(props: any, e: any) {
  props.validation.reset();
  const {
    store,
    store: { reportConfiguration },
    loader
  } = props;

  if (!reportConfiguration.isDirty) {
    store.updateAlertMessage(CommonMessages.MESSAGE_TYPE.INFO, localizer.getText('COMMON_MESSAGES_NO_CHANGES_FOUND'));
    return;
  }

  props
    .confirmFn('Confirmation', {
      description: localizer.getText('COMMON_MESSAGES_CONFIRM_CANCEL')
    })
    .then(() => {
      loader.startLoading();
      return store.onCancel().finally(loader.endLoading.bind(loader));
    });
}

/**
 * Check duplicate global and local names
 * @param {Object} store
 */
function validateDuplicateGlobalAndLocalNames(store: any) {
  const reportConfiguration = store.reportConfiguration;

  if (store.doesNeedToValidateGlobalAndLocalNames() && reportConfiguration.doesDuplicateGlobalAndLocalName()) {
    store.updateAlertMessage(CommonMessages.MESSAGE_TYPE.ERROR, constants.MESSAGES.M04);
    return false;
  }
  return true;
}

/**
 * On save handler
 * @param {Object} props
 * @param {Object} e
 */
function onSave(props: any, e: any) {
  const {
    store,
    store: { reportConfiguration },
    loader
  } = props;

  if (!reportConfiguration.isDirty) {
    store.updateAlertMessage(CommonMessages.MESSAGE_TYPE.INFO, localizer.getText('COMMON_MESSAGES_NO_CHANGES_FOUND'));
    return;
  }

  props.validation.validate().then(success => {
    success = success && validateDuplicateGlobalAndLocalNames(store);

    if (success) {
      loader.startLoading();
      return store
        .onSave()
        .then(result => {
          if (result.success) {
            store.updateAlertMessage(
              CommonMessages.MESSAGE_TYPE.SUCCESS,
              localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
            );
          } else {
            const messages = result.messages;
            if (reportConfiguration.recurrentReportSetting.once) {
              switch (messages[0].key) {
                case 'StartDate':
                  messages[0].key = 'atDate';
                  break;
                case 'StartTime':
                  messages[0].key = 'atTime';
                  break;
              }
            }
            props.validation.updateErrors(messages);
          }
        })
        .then(loader.endLoading.bind(loader));
    }
  });
}

/**
 * On clear archived version handler
 * @param {Object} props
 * @param {Object} e
 */
function onClearArchivedVersion(props: any, e: any) {
  e.stopPropagation();
  const { store, loader } = props;

  props
    .confirmFn('Confirmation', {
      description: localizer.getText('SYSTEM_CONFIGURATION_REPORT_SYSTEM_REMOVE_ALL_ARCHIVED')
    })
    .then(() => {
      loader.startLoading();
      return store.onClearArchivedVersion().finally(loader.endLoading.bind(loader));
    });
}

/**
 * On change time handler
 * @param {Object} props
 * @param {string} type
 * @param {string} value
 */
function onChangeTime(props: any, type: string, value: string) {
  const reportConfiguration = props.store.reportConfiguration;
  reportConfiguration.setValueByKey(type, value);
}

/**
 * On set enforce version history handler
 * @param {Object} props
 * @param {Object} e
 */
function setEnforceVersionHistory(props: any, e: any) {
  const value = e.target.checked;
  if (!value) {
    props
      .confirmFn('Confirmation', {
        description: localizer.getText('SYSTEM_CONFIGURATION_REPORT_ALL_ARCHIVED_WILL_REMOVED')
      })
      .then(() => {
        props.store.reportConfiguration.setValueByKey('enforceVersionHistory', false);
      });
  } else {
    props.store.reportConfiguration.setValueByKey('enforceVersionHistory', true);
  }
}

/**
 * On set num of archived version to keep
 * @param {Object} props
 * @param {Object} value
 */
function setNumOfArchivedVersionToKeep(props: any, value: any) {
  if (value === 0) {
    props.confirmFn('Information', {
      description: localizer.getText('SYSTEM_CONFIGURATION_REPORT_CONFIRM_AFTER_SAVING_SETTING'),
      type: 'info',
      showCloseIndicator: true
    });
  }
}

/**
 * On change default image handler
 * @param {Object} props
 * @param {Object} e
 */
function defaultImageUrlHander(props: any, e: any) {
  const model = props.store.reportConfiguration;
  model.setValueByKey.call(model, 'defaultImageUrl', e.target.value);
}

/**
 * On check remove archived versions handler
 * @param {Object} props
 * @param {Object} e
 */
function onCheckRemoveArchivedVersions(props: any, e: any) {
  const model = props.store.reportConfiguration;
  model.setValueByKey.call(model, 'removeArchivedVersions', e.target.checked);
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/SystemConfiguration/Report/Izenda.ReportConfigurationAction.js